import { AlertCircle, CheckCircle, Clock, Filter, RefreshCw, Search, Trash2, XCircle } from "lucide-react";
import React, { useEffect, useState } from "react";
import ConfirmationModal from "../../../components/ConfirmationModal";
import { useTheme } from "../../../contexts/ThemeContext";
import jobService, { JobExecution } from "../../../services/JobService";
import { formatNumber } from "../../../utils/numberUtils";
import JobDetailsModal from "./JobDetailsModal";

interface JobExecutionsTableProps {
  refreshTrigger?: number;
}

const JobExecutionsTable: React.FC<JobExecutionsTableProps> = ({ refreshTrigger = 0 }) => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  const [executions, setExecutions] = useState<JobExecution[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [jobNameFilter, setJobNameFilter] = useState<string>("");

  // Cleanup
  const [cleanupPeriod, setCleanupPeriod] = useState<string>("7d");
  const [isCleaning, setIsCleaning] = useState(false);

  // Modal state
  const [selectedExecution, setSelectedExecution] = useState<JobExecution | null>(null);

  // Confirmation modal states
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [jobToCancel, setJobToCancel] = useState<{ jobName: string; executionName: string } | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cleanupConfirmOpen, setCleanupConfirmOpen] = useState(false);

  // Action result message state
  const [actionMessage, setActionMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const fetchExecutions = async () => {
    try {
      setLoading(true);
      const response = await jobService.searchJobExecutions({
        status: statusFilter || undefined,
        jobName: jobNameFilter || undefined,
      });
      setExecutions(response.executions);
      setError(null);
    } catch (err: any) {
      setError(err.message || "Failed to fetch executions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExecutions();
  }, [statusFilter, jobNameFilter, refreshTrigger]);

  const openCancelConfirm = (jobName: string, executionName: string) => {
    setJobToCancel({ jobName, executionName });
    setCancelConfirmOpen(true);
  };

  const handleCancelConfirmed = async () => {
    if (!jobToCancel) return;

    try {
      setIsCancelling(true);
      await jobService.cancelJob(jobToCancel.jobName, jobToCancel.executionName);
      setCancelConfirmOpen(false);
      setJobToCancel(null);
      // Refresh list after short delay to allow status update
      setTimeout(fetchExecutions, 1000);
    } catch (err: any) {
      setActionMessage({ type: "error", text: `Failed to cancel job: ${err.message}` });
      setCancelConfirmOpen(false);
    } finally {
      setIsCancelling(false);
    }
  };

  const openCleanupConfirm = () => {
    setCleanupConfirmOpen(true);
  };

  const handleCleanupConfirmed = async () => {
    try {
      setIsCleaning(true);
      const result = await jobService.cleanupJobExecutions(cleanupPeriod);
      setActionMessage({ type: "success", text: `Cleanup successful. Deleted ${result.deletedCount} executions.` });
      setCleanupConfirmOpen(false);
      fetchExecutions();
    } catch (err: any) {
      setActionMessage({ type: "error", text: `Cleanup failed: ${err.message}` });
      setCleanupConfirmOpen(false);
    } finally {
      setIsCleaning(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "SUCCEEDED":
        return isDarkMode ? "bg-green-900/30 text-green-300" : "bg-green-100 text-green-800";
      case "FAILED":
        return isDarkMode ? "bg-red-900/30 text-red-300" : "bg-red-100 text-red-800";
      case "RUNNING":
        return isDarkMode ? "bg-blue-900/30 text-blue-300" : "bg-blue-100 text-blue-800";
      case "PENDING":
        return isDarkMode ? "bg-yellow-900/30 text-yellow-300" : "bg-yellow-100 text-yellow-800";
      case "CANCELLED":
        return isDarkMode ? "bg-rp-surface-raised text-rp-grey/70" : "bg-gray-100 text-gray-800";
      default:
        return isDarkMode ? "bg-rp-surface-raised text-rp-grey/70" : "bg-gray-100 text-gray-800";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "SUCCEEDED":
        return <CheckCircle className="w-3 h-3 mr-1.5" />;
      case "FAILED":
        return <AlertCircle className="w-3 h-3 mr-1.5" />;
      case "RUNNING":
        return <RefreshCw className="w-3 h-3 mr-1.5 animate-spin" />;
      case "PENDING":
        return <Clock className="w-3 h-3 mr-1.5" />;
      case "CANCELLED":
        return <XCircle className="w-3 h-3 mr-1.5" />;
      default:
        return null;
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="space-y-4">
      {/* Filters and Actions */}
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative">
            <Search
              className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${
                isDarkMode ? "text-rp-grey/50" : "text-gray-500"
              }`}
            />
            <input
              type="text"
              placeholder="Filter by Job Name"
              value={jobNameFilter}
              onChange={e => setJobNameFilter(e.target.value)}
              className={`pl-9 pr-4 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 ${
                isDarkMode
                  ? "focus:ring-rp-amber bg-rp-surface border-rp-separator text-rp-grey"
                  : "focus:ring-blue-500 bg-white border-gray-300 text-gray-900"
              }`}
            />
          </div>

          <div className="relative">
            <Filter
              className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${
                isDarkMode ? "text-rp-grey/50" : "text-gray-500"
              }`}
            />
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className={`pl-9 pr-8 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 appearance-none ${
                isDarkMode
                  ? "focus:ring-rp-amber bg-rp-surface border-rp-separator text-rp-grey"
                  : "focus:ring-blue-500 bg-white border-gray-300 text-gray-900"
              }`}
            >
              <option value="">All Statuses</option>
              <option value="RUNNING">Running</option>
              <option value="SUCCEEDED">Succeeded</option>
              <option value="FAILED">Failed</option>
              <option value="PENDING">Pending</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          </div>
        </div>

        <div className={`flex items-center gap-2 ${isDarkMode ? "border-rp-separator" : "border-gray-200"}`}>
          <span className={`text-sm ${isDarkMode ? "text-rp-grey/50" : "text-gray-500"}`}>Cleanup older than:</span>
          <select
            value={cleanupPeriod}
            onChange={e => setCleanupPeriod(e.target.value)}
            className={`text-sm border rounded-lg focus:outline-none focus:ring-2 ${
              isDarkMode
                ? "focus:ring-rp-amber bg-rp-surface border-rp-separator text-rp-grey"
                : "focus:ring-blue-500 bg-white border-gray-300 text-gray-900"
            }`}
          >
            <option value="12h">12 Hours</option>
            <option value="1d">1 Day</option>
            <option value="3d">3 Days</option>
            <option value="7d">7 Days</option>
            <option value="14d">14 Days</option>
            <option value="30d">30 Days</option>
          </select>
          <button
            onClick={openCleanupConfirm}
            disabled={isCleaning}
            className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 ${
              isDarkMode ? "bg-red-900/20 text-red-400 hover:bg-red-900/30" : "bg-red-50 text-red-600 hover:bg-red-100"
            }`}
          >
            <Trash2 className="w-4 h-4 mr-1.5" />
            Cleanup
          </button>
        </div>
      </div>

      {/* Action Message Banner */}
      {actionMessage && (
        <div
          className={`p-3 rounded-lg flex items-center justify-between ${
            actionMessage.type === "success"
              ? isDarkMode
                ? "bg-green-900/20 text-green-400"
                : "bg-green-50 text-green-700"
              : isDarkMode
              ? "bg-red-900/20 text-red-400"
              : "bg-red-50 text-red-700"
          }`}
        >
          <span className="text-sm">{actionMessage.text}</span>
          <button
            onClick={() => setActionMessage(null)}
            className={`ml-4 ${isDarkMode ? "hover:text-rp-grey" : "hover:text-gray-900"}`}
          >
            <XCircle className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className={`min-w-full divide-y ${isDarkMode ? "divide-rp-separator" : "divide-gray-200"}`}>
          <thead className={isDarkMode ? "bg-rp-surface-sunken" : "bg-gray-50"}>
            <tr>
              <th
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                  isDarkMode ? "text-rp-grey/70" : "text-gray-500"
                }`}
              >
                Job Name
              </th>
              <th
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                  isDarkMode ? "text-rp-grey/70" : "text-gray-500"
                }`}
              >
                Status
              </th>
              <th
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                  isDarkMode ? "text-rp-grey/70" : "text-gray-500"
                }`}
              >
                Started
              </th>
              <th
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                  isDarkMode ? "text-rp-grey/70" : "text-gray-500"
                }`}
              >
                Duration
              </th>
              <th
                className={`px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ${
                  isDarkMode ? "text-rp-grey/70" : "text-gray-500"
                }`}
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody className={`${isDarkMode ? "bg-rp-surface divide-rp-separator" : "bg-white divide-gray-200"} divide-y`}>
            {loading && executions.length === 0 ? (
              <tr>
                <td colSpan={5} className={`px-6 py-4 text-center ${isDarkMode ? "text-rp-grey/50" : "text-gray-500"}`}>
                  Loading executions...
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={5} className={`px-6 py-4 text-center ${isDarkMode ? "text-red-400" : "text-red-500"}`}>
                  {error}
                </td>
              </tr>
            ) : executions.length === 0 ? (
              <tr>
                <td colSpan={5} className={`px-6 py-4 text-center ${isDarkMode ? "text-rp-grey/50" : "text-gray-500"}`}>
                  No executions found matching criteria.
                </td>
              </tr>
            ) : (
              executions.map(exec => {
                // Use durationMs from API response
                const duration =
                  exec.durationMs != null
                    ? exec.durationMs < 1000
                      ? `${exec.durationMs}ms`
                      : `${formatNumber(exec.durationMs / 1000)}s`
                    : "-";

                return (
                  <tr
                    key={exec.id}
                    className={`${isDarkMode ? "hover:bg-rp-surface-raised text-rp-grey/70" : "hover:bg-gray-50 text-gray-900"}`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => setSelectedExecution(exec)}
                        className={`text-left text-sm font-medium hover:underline ${
                          isDarkMode ? "text-blue-400 hover:text-blue-300" : "text-blue-600 hover:text-blue-700"
                        }`}
                      >
                        {exec.jobName}
                      </button>
                      <div
                        className={`text-xs ${isDarkMode ? "text-rp-grey/50" : "text-gray-500"}`}
                        title={exec.executionName}
                      >
                        ID: {exec.executionName.split("/").pop()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                          exec.status
                        )}`}
                      >
                        {getStatusIcon(exec.status)}
                        {exec.status}
                      </span>
                    </td>
                    <td
                      className={`px-6 py-4 whitespace-nowrap text-sm ${
                        isDarkMode ? "text-rp-grey/50" : "text-gray-500"
                      }`}
                    >
                      {formatDate(exec.startedAt || exec.createdDatetimeUtc)}
                    </td>
                    <td
                      className={`px-6 py-4 whitespace-nowrap text-sm ${
                        isDarkMode ? "text-rp-grey/50" : "text-gray-500"
                      }`}
                    >
                      {duration}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {(exec.status === "RUNNING" || exec.status === "PENDING") && (
                        <button
                          onClick={() => openCancelConfirm(exec.jobName, exec.executionName)}
                          className={`${
                            isDarkMode ? "text-red-400 hover:text-red-300" : "text-red-600 hover:text-red-900"
                          }`}
                        >
                          Cancel
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Job Details Modal */}
      <JobDetailsModal execution={selectedExecution} onClose={() => setSelectedExecution(null)} />

      {/* Cancel Job Confirmation Modal */}
      <ConfirmationModal
        isOpen={cancelConfirmOpen}
        onClose={() => {
          setCancelConfirmOpen(false);
          setJobToCancel(null);
        }}
        onConfirm={handleCancelConfirmed}
        title="Cancel Job"
        message="Are you sure you want to cancel this job?"
        confirmText="Cancel Job"
        cancelText="Keep Running"
        isDestructive={true}
        loading={isCancelling}
      />

      {/* Cleanup Confirmation Modal */}
      <ConfirmationModal
        isOpen={cleanupConfirmOpen}
        onClose={() => setCleanupConfirmOpen(false)}
        onConfirm={handleCleanupConfirmed}
        title="Cleanup Old Executions"
        message={`Delete completed jobs older than ${cleanupPeriod}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        isDestructive={true}
        loading={isCleaning}
      />
    </div>
  );
};

export default JobExecutionsTable;
